/**
 * Project: DAL (Digital Abstraction Layer)
 * Description: CPQ → Indirect Partners → LMaaS Metros management page.
 *              Lists metros with ZIP counts and an enabled toggle, supports
 *              adding a metro (created disabled), and — per metro — viewing its
 *              ZIPs and replacing them via a full-replace CSV upload.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, MapPin, Plus, RefreshCw, ShieldAlert, Trash2, Upload } from 'lucide-react'
import { toast } from 'react-toastify'
import { api, type LmaasMetro } from '@services/api'

const errMessage = (e: unknown, fallback: string): string => {
  const resp = (e as { response?: { data?: { error?: string } } })?.response
  return resp?.data?.error ?? fallback
}

interface PostcodeConflict {
  postcode: string
  metroCode: string
}

/** Pull the per-ZIP conflict list off a 409 upload response, if present. */
const errConflicts = (e: unknown): PostcodeConflict[] => {
  const conflicts = (e as { response?: { data?: { conflicts?: unknown } } })?.response?.data?.conflicts
  if (!Array.isArray(conflicts)) return []
  return conflicts.filter(
    (c): c is PostcodeConflict =>
      typeof (c as PostcodeConflict)?.postcode === 'string' && typeof (c as PostcodeConflict)?.metroCode === 'string',
  )
}

const LmaasMetros: React.FC = () => {
  const [metros, setMetros] = useState<LmaasMetro[]>([])
  const [loading, setLoading] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [selected, setSelected] = useState<LmaasMetro | null>(null)

  // Add-metro form
  const [showAdd, setShowAdd] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')

  const loadMetros = useCallback(async () => {
    setLoading(true)
    try {
      setMetros(await api.getLmaasMetros())
      setForbidden(false)
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) {
        setMetros([])
        setForbidden(true)
        return
      }
      toast.error(errMessage(e, 'Failed to load metros'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadMetros()
  }, [loadMetros])

  const toggleEnabled = useCallback(
    async (metro: LmaasMetro) => {
      try {
        await api.updateLmaasMetro(metro.code, { enabled: !metro.enabled })
        toast.success(`${metro.code} ${metro.enabled ? 'disabled' : 'enabled'}`)
        await loadMetros()
      } catch (e) {
        toast.error(errMessage(e, 'Failed to update metro'))
      }
    },
    [loadMetros],
  )

  const addMetro = useCallback(async () => {
    const code = newCode.trim().toUpperCase()
    const name = newName.trim()
    if (!code || !name) {
      toast.error('Code and name are required')
      return
    }
    try {
      await api.createLmaasMetro({ code, name })
      toast.success(`Created ${code} (disabled)`)
      setShowAdd(false)
      setNewCode('')
      setNewName('')
      await loadMetros()
    } catch (e) {
      toast.error(errMessage(e, 'Failed to create metro'))
    }
  }, [newCode, newName, loadMetros])

  const deleteMetro = useCallback(
    async (metro: LmaasMetro) => {
      if (!window.confirm(`Delete metro ${metro.code}? Its ${metro.zipCount} ZIP(s) will be removed.`)) return
      try {
        await api.deleteLmaasMetro(metro.code)
        toast.success(`Deleted ${metro.code}`)
        await loadMetros()
      } catch (e) {
        toast.error(errMessage(e, 'Failed to delete metro'))
      }
    },
    [loadMetros],
  )

  if (forbidden) {
    return (
      <div className="p-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-800 dark:text-gray-200">
          <MapPin className="h-6 w-6 text-blue-600" />
          LMaaS Metros
        </h1>
        <div
          role="alert"
          className="mt-6 flex max-w-2xl gap-3 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-900/30"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <h2 className="font-semibold text-red-900 dark:text-red-300">Access denied</h2>
            <p className="mt-1 text-sm text-red-800 dark:text-red-300">
              You don't have permission to manage LMaaS metros.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (selected) {
    return (
      <MetroDetail
        metro={selected}
        onBack={() => {
          setSelected(null)
          void loadMetros()
        }}
      />
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-800 dark:text-gray-200">
            <MapPin className="h-6 w-6 text-blue-600" />
            LMaaS Metros
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage the Equinix LMaaS serviceable footprint. A metro is served only once enabled.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void loadMetros()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add metro
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-white/5">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Code</label>
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="NY"
              maxLength={10}
              className="mt-1 w-28 rounded border border-gray-300 px-2 py-1.5 text-sm uppercase dark:border-gray-600"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New York"
              className="mt-1 w-64 rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600"
            />
          </div>
          <button
            onClick={() => void addMetro()}
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Create
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">New metros are disabled by default.</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                Code
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                ZIPs
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                Enabled
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {metros.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No metros yet. Add one, then import its ZIPs.
                </td>
              </tr>
            )}
            {metros.map((metro) => (
              <tr key={metro.code} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-gray-100">
                  {metro.code}
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700 dark:text-gray-300">{metro.name}</td>
                <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700 dark:text-gray-300">
                  {metro.zipCount}
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap">
                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={metro.enabled}
                      onChange={() => void toggleEnabled(metro)}
                      className="peer sr-only"
                    />
                    <div className="peer relative h-5 w-9 rounded-full bg-gray-300 peer-checked:bg-green-500 after:absolute after:mt-0.5 after:ml-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4 dark:bg-gray-600"></div>
                  </label>
                </td>
                <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                  <button
                    onClick={() => setSelected(metro)}
                    className="mr-3 font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Manage ZIPs
                  </button>
                  <button
                    onClick={() => void deleteMetro(metro)}
                    disabled={metro.enabled}
                    title={metro.enabled ? 'Disable the metro before deleting' : 'Delete metro'}
                    className="text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300 dark:hover:text-red-300"
                  >
                    <Trash2 className="inline h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Metro detail: ZIP list + full-replace CSV upload ───────────────────────

const MetroDetail: React.FC<{ metro: LmaasMetro; onBack: () => void }> = ({ metro, onBack }) => {
  const [postcodes, setPostcodes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [conflicts, setConflicts] = useState<PostcodeConflict[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPostcodes(await api.getLmaasMetroPostcodes(metro.code))
    } catch (e) {
      toast.error(errMessage(e, 'Failed to load ZIPs'))
    } finally {
      setLoading(false)
    }
  }, [metro.code])

  useEffect(() => {
    void load()
  }, [load])

  const onFile = useCallback(
    async (file: File) => {
      if (
        !window.confirm(
          `Replace ALL ZIPs for ${metro.code} with the contents of "${file.name}"? ` +
            `This removes every current ZIP for this metro and inserts only what the file contains.`,
        )
      ) {
        if (fileRef.current) fileRef.current.value = ''
        return
      }
      setUploading(true)
      try {
        const result = await api.uploadLmaasMetroPostcodes(metro.code, file)
        setConflicts([])
        toast.success(`Imported ${result.zipCount} ZIP(s) for ${metro.code}`)
        await load()
      } catch (e) {
        const found = errConflicts(e)
        setConflicts(found)
        if (found.length > 0) {
          toast.error(`${found.length} ZIP(s) already belong to another metro — see details below. Nothing changed.`)
        } else {
          toast.error(errMessage(e, 'Failed to import ZIPs'))
        }
      } finally {
        setUploading(false)
        if (fileRef.current) fileRef.current.value = ''
      }
    },
    [metro.code, load],
  )

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-300"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to metros
      </button>

      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl text-gray-800 dark:text-gray-200">
          <strong className="mr-2 rounded-xl bg-blue-600 px-2 py-0.5 text-white">{metro.code}</strong>
          {metro.name}
        </h1>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
          <Upload className="h-4 w-4" />
          {uploading ? 'Importing…' : 'Replace ZIPs (CSV)'}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
        </label>
      </div>

      <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
        Uploading a CSV performs a full replace of this metro&apos;s ZIP list. One 5-digit ZIP per line.
      </p>

      {conflicts.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-900/30">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              Import rejected: {conflicts.length} ZIP(s) already belong to another metro.
            </p>
            <button
              onClick={() => setConflicts([])}
              className="text-sm text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-300"
            >
              Dismiss
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {conflicts.map((c) => (
              <span
                key={c.postcode}
                className="rounded bg-red-100 px-2 py-0.5 font-mono text-xs text-red-800 dark:bg-red-900/40 dark:text-red-300"
                title={`${c.postcode} is owned by ${c.metroCode}`}
              >
                {c.postcode} → {c.metroCode}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : postcodes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No ZIPs yet. Import a CSV to populate this metro.</p>
        ) : (
          <>
            <p className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">{postcodes.length} ZIP(s)</p>
            <div className="flex flex-wrap gap-1.5">
              {postcodes.map((zip) => (
                <span
                  key={zip}
                  className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                >
                  {zip}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LmaasMetros
